import { getCurrentScope, onScopeDispose, ref, type Ref } from "vue";
import { io, type Socket } from "socket.io-client";

/**
 * Canali socket verso service-socket (server socket.io).
 *
 * Un "canale" è un namespace, eventualmente con una query di handshake. Oggi service-socket espone
 * solo il namespace radice e assegna da sé le room all'handshake (user:<sub>): job dei report e
 * notifiche viaggiano sulla stessa connessione come eventi distinti ("report-job", "notification").
 * Il composable non conosce nessuno dei domini: apre, ascolta, chiude.
 *
 * Autenticazione: service-socket rifiuta l'handshake senza il cookie `hd-context`, emesso da
 * POST /context al bootstrap (`loadUserContext` gira prima di `app.mount`). Il cookie lo allega
 * il browser da sé, essendo su `.harleydikkinson.com`: qui non c'è nulla da passare, ma se quella
 * chiamata di bootstrap è fallita il server rifiuta e il canale lo segnala in `lastError`.
 */

const defaultUrl: string = import.meta.env.VITE_SOCKET_URL ?? "";
const defaultPath: string = import.meta.env.VITE_SOCKET_PATH ?? "";

/** Un handler registrato: il payload lo tipizza chi chiama `on` */
type SocketEventHandler = (...args: unknown[]) => void;

export interface SocketChannelOptions {
  /** Namespace socket.io. Default: namespace radice (l'unico esposto oggi da service-socket) */
  namespace?: string;
  /** Query di handshake (per i report: `{ room }`). La sua presenza forza una connessione dedicata */
  query?: Record<string, string>;
  /** Override di VITE_SOCKET_URL */
  url?: string;
  /** Override di VITE_SOCKET_PATH (staging/produzione: "/socket/socket.io") */
  path?: string;
}

export interface SocketChannel {
  isConnected: Ref<boolean>;
  lastError: Ref<string | null>;
  connect: () => void;
  disconnect: () => void;
  on: <T>(event: string, handler: (payload: T) => void) => void;
  off: (event: string, handler?: (payload: never) => void) => void;
  emit: (event: string, payload?: unknown) => void;
}

/**
 * Canale con ciclo di vita a carico di chi lo crea: serve per le connessioni dinamiche
 * (una per job) aperte dentro un'action, dove `onScopeDispose` non avrebbe alcuno scope attivo.
 * Chi lo crea DEVE chiamare `disconnect()`.
 */
export function createSocketChannel(options: SocketChannelOptions = {}): SocketChannel {
  const isConnected = ref<boolean>(false);
  const lastError = ref<string | null>(null);

  /** Registro dei listener: sopravvive alla connessione, così `on` funziona anche prima di `connect` */
  const listeners = new Map<string, Set<SocketEventHandler>>();

  let socket: Socket | null = null;

  const connect = (): void => {
    if (socket) return;

    const baseUrl = (options.url ?? defaultUrl).replace(/\/+$/, "");
    if (!baseUrl) {
      // Ambiente senza socket (staging e produzione non espongono ancora la route): condizione
      // prevista, non un errore di programmazione. Chi consuma deve poter ripiegare sul REST.
      lastError.value = "URL del socket non configurato (VITE_SOCKET_URL)";
      console.warn(`[useSocket] ${lastError.value}: canale non avviato`);
      return;
    }

    const path = options.path ?? defaultPath;
    const query = options.query;
    // La query viaggia nell'handshake, che socket.io condivide fra i canali dello stesso host:
    // riusando il Manager in cache le opzioni della seconda chiamata vengono ignorate e il server
    // vedrebbe la query di un altro canale. Un canale con query vuole una connessione propria.
    const needsOwnConnection = !!query && Object.keys(query).length > 0;

    socket = io(`${baseUrl}${options.namespace ?? ""}`, {
      transports: ["websocket"],
      // Ininfluente sul transport websocket, dove i cookie li allega il browser: engine.io lo usa
      // solo per il polling (xhr.withCredentials / fetch credentials). Resta perché l'handshake è
      // autenticato col cookie hd-context e senza di esso il polling, se riabilitato, lo perderebbe.
      withCredentials: true,
      forceNew: needsOwnConnection,
      ...(needsOwnConnection ? { query } : {}),
      ...(path ? { path } : {}),
    });

    socket.on("connect", () => {
      isConnected.value = true;
      lastError.value = null;
    });

    socket.on("disconnect", (reason: string) => {
      isConnected.value = false;

      // service-socket chiude la sessione dopo circa un'ora ("session:expired") per rieseguire
      // l'handshake: dopo un disconnect deciso dal server socket.io-client non riprova da sé,
      // quindi senza questo il canale morirebbe in silenzio.
      if (reason === "io server disconnect") socket?.connect();
    });

    socket.on("connect_error", (error: Error) => {
      isConnected.value = false;

      // Rifiuto applicativo: socket.io ha già distrutto il socket, quindi non ritenterà. Porta
      // un codice in `data` (invalid_context, too_many_connections, …) che è il dato più utile.
      // Un errore di trasporto lascia invece `active` a true e il Manager riprova da solo — ed è
      // anche la forma con cui si manifesta il 403 dell'edge, che non arriva mai fin qui.
      const isRefused = socket?.active === false;
      const code = (error as { data?: { code?: string } }).data?.code;
      const detail = code ? `${code} (${error.message})` : error.message;

      lastError.value = isRefused ? `handshake rifiutato dal server: ${detail}` : detail;

      console.warn(
        isRefused
          ? `[useSocket] handshake rifiutato (nessun ritentativo): ${detail}.`
          : `[useSocket] connessione fallita, ritentativo in corso: ${detail}. ` +
              "Se persiste, la sessione hd-context potrebbe essere assente o scaduta: " +
              "il rifiuto dell'edge arriva come errore di trasporto."
      );
    });

    // i listener registrati prima della connessione si agganciano ora
    listeners.forEach((handlers, event) => {
      handlers.forEach((handler) => socket?.on(event, handler));
    });
  };

  const disconnect = (): void => {
    if (!socket) return;

    // via gli handler prima di chiudere: una riconnessione non deve trovarne di orfani
    listeners.forEach((handlers, event) => {
      handlers.forEach((handler) => socket?.off(event, handler));
    });
    socket.close();
    socket = null;
    isConnected.value = false;
  };

  const on = <T>(event: string, handler: (payload: T) => void): void => {
    if (!event) throw new Error("useSocket.on: event is required");

    const registered = handler as SocketEventHandler;
    const handlers = listeners.get(event) ?? new Set<SocketEventHandler>();
    handlers.add(registered);
    listeners.set(event, handlers);

    socket?.on(event, registered);
  };

  const off = (event: string, handler?: (payload: never) => void): void => {
    if (!event) throw new Error("useSocket.off: event is required");

    const handlers = listeners.get(event);
    if (!handlers) return;

    if (!handler) {
      handlers.forEach((registered) => socket?.off(event, registered));
      listeners.delete(event);
      return;
    }

    const registered = handler as SocketEventHandler;
    handlers.delete(registered);
    socket?.off(event, registered);
    if (!handlers.size) listeners.delete(event);
  };

  const emit = (event: string, payload?: unknown): void => {
    if (!event) throw new Error("useSocket.emit: event is required");

    if (!socket) {
      console.warn(`[useSocket] emit "${event}" ignorato: canale non connesso`);
      return;
    }

    socket.emit(event, payload);
  };

  return { isConnected, lastError, connect, disconnect, on, off, emit };
}

/**
 * Canale legato allo scope corrente: si disconnette da solo quando il componente viene smontato
 * o lo store viene disposto. Vale dentro un `setup` (componente o store Pinia); dentro un'action
 * non c'è scope attivo, e lì va usata `createSocketChannel` con `disconnect()` esplicito.
 */
export function useSocket(options: SocketChannelOptions = {}): SocketChannel {
  const channel = createSocketChannel(options);

  if (getCurrentScope()) {
    onScopeDispose(() => channel.disconnect());
  }

  return channel;
}
